import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useRole, UserRole } from '../contexts/RoleContext';

interface RoleGuardProps {
  requiredRole: UserRole | UserRole[];
  children: React.ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({ requiredRole, children }) => {
  const { hasPermission, isLoading } = useRole();
  const [showDenied, setShowDenied] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Check if user has required permission (only valid after loading completes)
  const isAuthorized = !isLoading && hasPermission(requiredRole);

  useEffect(() => {
    // Don't show denied while still loading the role
    if (isLoading) return;

    if (!hasPermission(requiredRole)) {
      // Show permission denied message
      setShowDenied(true);
      
      // After 3 seconds, redirect to dashboard
      const timer = setTimeout(() => {
        setShowDenied(false);
        setShouldRedirect(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isLoading, requiredRole]);

  // Redirect to dashboard if unauthorized
  if (shouldRedirect) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show permission denied message
  if (showDenied) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Permission Denied
          </h2>
          <p className="text-center text-gray-600 mb-4">
            You do not have permission to access this page.
          </p>
          <p className="text-center text-sm text-gray-500">
            Redirecting to dashboard in 3 seconds...
          </p>
        </div>
      </div>
    );
  }

  // Render children if authorized
  if (isAuthorized) {
    return <>{children}</>;
  }

  // Default: don't render anything while checking
  return null;
};
